<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS, ATTACHMENT_TYPES } from './constants';

const emit = defineEmits(['retry']);

const { status, createdAt, content, attachments, contentAttributes } =
  useMessageContext();

const { t } = useI18n();

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});

// Aceleres: o erro cru do provedor (content_attributes.external_error, ex.:
// "Timed out reading data from server", "400 Bad Request") nunca chega ao
// atendente. Ele segue gravado na mensagem para o suporte consultar; aqui só
// mostramos um rótulo amigável escolhido pelo tipo de mídia que falhou.
const failureLabel = computed(() => {
  const attachment = attachments.value?.[0];
  if (!attachment) return t('CHAT_LIST.FAILED_TO_SEND');

  // Documentos enviados pela WAME podem chegar como áudio/imagem; a intenção
  // original em wame_media_type prevalece, igual à escolha da bolha feita em
  // Message.vue, para o rótulo não contradizer o card renderizado.
  const wameMediaType = String(
    contentAttributes.value?.wameMediaType ??
      contentAttributes.value?.wame_media_type ??
      ''
  ).toLowerCase();
  if (wameMediaType === 'document') return t('CHAT_LIST.FAILED_TO_SEND_FILE');

  switch (attachment.fileType) {
    case ATTACHMENT_TYPES.IMAGE:
      return t('CHAT_LIST.FAILED_TO_SEND_IMAGE');
    case ATTACHMENT_TYPES.VIDEO:
    case ATTACHMENT_TYPES.IG_REEL:
      return t('CHAT_LIST.FAILED_TO_SEND_VIDEO');
    case ATTACHMENT_TYPES.AUDIO:
      return t('CHAT_LIST.FAILED_TO_SEND_AUDIO');
    case ATTACHMENT_TYPES.FILE:
      return t('CHAT_LIST.FAILED_TO_SEND_FILE');
    default:
      return t('CHAT_LIST.FAILED_TO_SEND');
  }
});
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <Icon icon="i-lucide-alert-triangle" class="text-n-ruby-11 size-[14px]" />
    <span>{{ failureLabel }}</span>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      :title="t('CHAT_LIST.RETRY_SEND')"
      :aria-label="t('CHAT_LIST.RETRY_SEND')"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>
